import type { ReactNode } from 'react'
import { numberFmt } from '@/lib/format'
import { niceMax, sequential } from './chart-utils'
import { useTip } from './use-tip'

/*
 * The redesign's charts, drawn as SVG from one scale each, so every bar, tick and label agrees.
 * Colours come from the validated --viz-* tokens: olive, plum, rust, blue, then grey for "Other".
 */

export function TipRows({
  title,
  rows,
}: {
  title: string
  rows: { label: string; value: string; color?: string; bold?: boolean }[]
}) {
  return (
    <>
      <b>{title}</b>
      {rows.map((r) => (
        <div key={r.label} className="row" style={r.bold ? { marginTop: 4, fontWeight: 600 } : undefined}>
          <span>
            {r.color && <i style={{ background: r.color }} />}
            {r.label}
          </span>
          {r.value}
        </div>
      ))}
    </>
  )
}

export function Legend({ keys }: { keys: { label: string; color: string }[] }) {
  return (
    <div className="legend">
      {keys.map((k) => (
        <span key={k.label}>
          <i style={{ background: k.color }} />
          {k.label}
        </span>
      ))}
    </div>
  )
}

/** A small trend line with a soft fill and the latest point marked. */
export function Spark({ values, color = 'var(--viz-1)' }: { values: number[]; color?: string }) {
  if (values.length < 2) return null
  const w = 160
  const h = 34
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const pts = values.map(
    (v, i) => [(i / (values.length - 1)) * w, h - 3 - ((v - min) / span) * (h - 8)] as const,
  )
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
  const last = pts[pts.length - 1] ?? ([0, 0] as const)
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={`${line} L${w} ${h} L0 ${h} Z`} fill={color} fillOpacity={0.12} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle
        cx={last[0]}
        cy={last[1]}
        r={3}
        fill={color}
        stroke="var(--surface)"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

export interface StackKey {
  key: string
  label: string
  color: string
}

/** Stacked columns over time; one column per period, segments in the keys' order. */
export function StackedBars({
  rows,
  keys,
  width = 640,
  height = 230,
  format = (v: number) => numberFmt.format(v),
  label = 'Stacked column chart',
}: {
  rows: { label: string; values: Record<string, number> }[]
  keys: StackKey[]
  width?: number
  height?: number
  format?: (v: number) => string
  label?: string
}) {
  const { bind, node } = useTip()
  const padL = 46
  const padB = 26
  const padT = 10
  const padR = 6
  const totals = rows.map((r) => keys.reduce((a, k) => a + Math.max(0, r.values[k.key] ?? 0), 0))
  const max = niceMax(Math.max(1, ...totals) * 1.05)
  const iw = width - padL - padR
  const ih = height - padT - padB
  const bw = rows.length ? iw / rows.length : iw
  const y = (v: number) => padT + ih - (v / max) * ih
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max)
  const every = rows.length > 14 ? Math.ceil(rows.length / 12) : 1
  return (
    <>
      <svg className="chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
        {ticks.map((t) => (
          <g key={t}>
            <line className="gridline" x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} />
            <text x={padL - 8} y={y(t) + 4} textAnchor="end">
              {format(t)}
            </text>
          </g>
        ))}
        {rows.map((r, i) => {
          let acc = 0
          const x = padL + i * bw + bw * 0.18
          const bwi = bw * 0.64
          const present = keys.filter((k) => (r.values[k.key] ?? 0) > 0)
          return (
            <g key={`${r.label}-${i}`}>
              {present.map((k, ki) => {
                const v = r.values[k.key] ?? 0
                const y0 = y(acc)
                const y1 = y(acc + v)
                acc += v
                const top = ki === present.length - 1
                const h = Math.max(0, y0 - y1 - (top ? 0 : 1.5))
                return top ? (
                  <path
                    key={k.key}
                    d={`M${x} ${y0} V${y1 + Math.min(4, h)} Q${x} ${y1} ${x + 4} ${y1} H${x + bwi - 4} Q${x + bwi} ${y1} ${x + bwi} ${y1 + Math.min(4, h)} V${y0} Z`}
                    fill={k.color}
                  />
                ) : (
                  <rect key={k.key} x={x} y={y1} width={bwi} height={h} fill={k.color} />
                )
              })}
              <rect
                x={padL + i * bw}
                y={padT}
                width={bw}
                height={ih}
                fill="transparent"
                {...bind(
                  <TipRows
                    title={r.label}
                    rows={[
                      ...keys.map((k) => ({
                        label: k.label,
                        value: format(r.values[k.key] ?? 0),
                        color: k.color,
                      })),
                      { label: 'Total', value: format(totals[i] ?? 0), bold: true },
                    ]}
                  />,
                )}
              />
              {i % every === 0 && (
                <text x={padL + i * bw + bw / 2} y={height - 8} textAnchor="middle">
                  {r.label}
                </text>
              )}
            </g>
          )
        })}
        <line className="base" x1={padL} x2={width - padR} y1={y(0)} y2={y(0)} />
      </svg>
      {node}
    </>
  )
}

/** One series over time as a soft area with the latest point marked. */
export function AreaChart({
  points,
  labels,
  color = 'var(--viz-1)',
  width = 640,
  height = 220,
  format = (v: number) => numberFmt.format(v),
  label = 'Area chart',
}: {
  points: number[]
  labels: string[]
  color?: string
  width?: number
  height?: number
  format?: (v: number) => string
  label?: string
}) {
  const { bind, node } = useTip()
  if (points.length < 2) return null
  const padL = 50
  const padB = 26
  const padT = 12
  const padR = 10
  const max = niceMax(Math.max(1, ...points) * 1.08)
  const iw = width - padL - padR
  const ih = height - padT - padB
  const x = (i: number) => padL + (i / (points.length - 1)) * iw
  const y = (v: number) => padT + ih - (v / max) * ih
  const line = points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')
  const every = points.length > 14 ? Math.ceil(points.length / 12) : 1
  const slot = iw / points.length
  return (
    <>
      <svg className="chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line className="gridline" x1={padL} x2={width - padR} y1={y(f * max)} y2={y(f * max)} />
            <text x={padL - 8} y={y(f * max) + 4} textAnchor="end">
              {format(f * max)}
            </text>
          </g>
        ))}
        <path
          d={`${line} L${x(points.length - 1)} ${y(0)} L${padL} ${y(0)} Z`}
          fill={color}
          fillOpacity={0.14}
        />
        <path d={line} fill="none" stroke={color} strokeWidth={2.25} strokeLinejoin="round" />
        {points.map((v, i) => (
          <g key={i}>
            <rect
              x={x(i) - slot / 2}
              y={padT}
              width={slot}
              height={ih}
              fill="transparent"
              {...bind(
                <TipRows title={labels[i] ?? ''} rows={[{ label: 'Value', value: format(v), color }]} />,
              )}
            />
            {i % every === 0 && (
              <text x={x(i)} y={height - 8} textAnchor="middle">
                {labels[i]}
              </text>
            )}
          </g>
        ))}
        <circle
          cx={x(points.length - 1)}
          cy={y(points[points.length - 1] ?? 0)}
          r={4.5}
          fill={color}
          stroke="var(--surface)"
          strokeWidth={2}
        />
        <line className="base" x1={padL} x2={width - padR} y1={y(0)} y2={y(0)} />
      </svg>
      {node}
    </>
  )
}

/**
 * Where recurring revenue moved: the starting figure, the four kinds of change on their own scale
 * (new, upgrades, downgrades, cancelled) with the net change, then where it is now.
 */
export function MovementChart({
  start,
  now,
  moves,
  startLabel,
  nowLabel = 'Today',
  format,
}: {
  start: number
  now: number
  moves: { label: string; value: number }[]
  startLabel: string
  nowLabel?: string
  format: (v: number) => string
}) {
  const net = now - start
  const items = [...moves, { label: 'Net change', value: net }]
  const w = 640
  const h = 190
  const mid = 95
  const padT = 22
  const max = niceMax(Math.max(1, ...items.map((i) => Math.abs(i.value))) * 1.1)
  const sc = (v: number) => (Math.abs(v) / max) * (mid - padT)
  const bw = w / items.length
  const change = start > 0 ? (net / start) * 100 : null
  return (
    <div className="mv">
      <div className="mv-end">
        <small>{startLabel}</small>
        <b>{format(start)}</b>
      </div>
      <svg
        className="chart bridge"
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label="Changes to recurring revenue"
      >
        <line className="base" x1={0} x2={w} y1={mid} y2={mid} />
        {items.map((it, i) => {
          const x = i * bw + bw * 0.24
          const bwi = bw * 0.52
          const hh = Math.max(2, sc(it.value))
          const y = it.value >= 0 ? mid - hh : mid
          const fill = i === items.length - 1 ? 'var(--ink)' : it.value >= 0 ? 'var(--viz-1)' : 'var(--viz-3)'
          return (
            <g key={it.label}>
              <rect x={x} y={y} width={bwi} height={hh} rx={4} fill={fill} />
              <text className="v" x={x + bwi / 2} y={it.value >= 0 ? y - 7 : y + hh + 15} textAnchor="middle">
                {it.value >= 0 ? '+' : '−'}
                {format(Math.abs(it.value))}
              </text>
              <text x={x + bwi / 2} y={h - 6} textAnchor="middle">
                {it.label}
              </text>
            </g>
          )
        })}
      </svg>
      <div className="mv-end" style={{ textAlign: 'right' }}>
        <small>{nowLabel}</small>
        <b>{format(now)}</b>
        {change !== null && (
          <span style={{ color: net >= 0 ? 'var(--ok)' : 'var(--bad)' }}>
            {net >= 0 ? '+' : '−'}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  )
}

/** A ring split into slices, with the headline in the middle. */
export function Donut({
  parts,
  label,
  sub,
  size = 132,
}: {
  parts: { label: string; value: number; color: string }[]
  label: string
  sub: string
  size?: number
}) {
  const total = parts.reduce((a, p) => a + p.value, 0) || 1
  const r = 52
  const c = 2 * Math.PI * r
  const shown = parts.filter((x) => x.value > 0).length
  const offsets = parts.map((_, i) => parts.slice(0, i).reduce((a, p) => a + (p.value / total) * c, 0))
  return (
    <svg viewBox="0 0 132 132" width={size} height={size} role="img" aria-label={`${label} ${sub}`}>
      <circle cx={66} cy={66} r={r} fill="none" stroke="var(--sunken)" strokeWidth={16} />
      {parts.map((p, i) => {
        const len = (p.value / total) * c
        const gap = shown > 1 ? 2.5 : 0
        return (
          <circle
            key={p.label}
            cx={66}
            cy={66}
            r={r}
            fill="none"
            stroke={p.color}
            strokeWidth={16}
            strokeDasharray={`${Math.max(0, len - gap)} ${c}`}
            strokeDashoffset={-(offsets[i] ?? 0)}
            transform="rotate(-90 66 66)"
          />
        )
      })}
      <text
        x={66}
        y={64}
        textAnchor="middle"
        style={{ font: '500 22px var(--f-display)', fill: 'var(--ink)' }}
      >
        {label}
      </text>
      <text x={66} y={82} textAnchor="middle" style={{ font: '11px var(--f-sans)', fill: 'var(--subtle)' }}>
        {sub}
      </text>
    </svg>
  )
}

export function DonutLegend({
  parts,
  format = (v: number) => numberFmt.format(v),
}: {
  parts: { label: string; value: number; color: string }[]
  format?: (v: number) => string
}) {
  return (
    <div className="legend">
      {parts.map((p) => (
        <span key={p.label}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i style={{ background: p.color }} />
            {p.label}
          </span>
          <b>{format(p.value)}</b>
        </span>
      ))}
    </div>
  )
}

export interface BarRow {
  key: string
  label: string
  value: number
  sub?: string
  color?: string
  lead?: ReactNode
}

/** Ranked horizontal bars, longest first as given. */
export function BarList({
  rows,
  format = (v: number) => numberFmt.format(v),
  labelWidth,
  valueWidth,
  onSelect,
  active,
}: {
  rows: BarRow[]
  format?: (v: number) => string
  labelWidth?: number
  valueWidth?: number
  onSelect?: (key: string) => void
  active?: string | null
}) {
  const max = Math.max(1, ...rows.map((r) => r.value))
  const style =
    labelWidth || valueWidth
      ? { gridTemplateColumns: `minmax(0,${labelWidth ?? 150}px) minmax(0,1fr) ${valueWidth ?? 54}px` }
      : undefined
  return (
    <div className="barlist">
      {rows.map((r) => {
        const body = (
          <>
            <span className="bl-n">
              {r.lead}
              <span className="truncate">{r.label}</span>
            </span>
            <span className="bl-tr">
              <span style={{ width: `${(r.value / max) * 100}%`, background: r.color ?? 'var(--viz-1)' }} />
            </span>
            <span className="bl-v">
              {format(r.value)}
              {r.sub && <small>{r.sub}</small>}
            </span>
          </>
        )
        return onSelect ? (
          <button
            key={r.key}
            type="button"
            className={`bl text-left ${active === r.key ? 'on' : ''}`}
            style={style}
            onClick={() => onSelect(r.key)}
            aria-pressed={active === r.key}
          >
            {body}
          </button>
        ) : (
          <div key={r.key} className="bl" style={style}>
            {body}
          </div>
        )
      })}
    </div>
  )
}

/** How far a group got, step by step, with a note where more than a quarter stop. */
export function Funnel({
  steps,
  unit = 'workspaces',
}: {
  steps: { label: string; count: number }[]
  unit?: string
}) {
  const top = steps[0]?.count || 1
  return (
    <div className="funnel">
      {steps.map((s, i) => {
        const prev = i ? (steps[i - 1]?.count ?? 0) : s.count
        const lost = prev - s.count
        const share = (s.count / top) * 100
        const last = i === steps.length - 1
        return (
          <div key={s.label} style={{ display: 'contents' }}>
            {i > 0 && prev > 0 && lost / prev > 0.25 && (
              <div className="drop">↳ {Math.round((lost / prev) * 100)}% stop here</div>
            )}
            <div className="fs">
              <span>{s.label}</span>
              <span className="fs-bar">
                <span style={{ width: `${share}%`, ...(last ? { background: 'var(--viz-2)' } : {}) }} />
                {share > 16 && <em style={last ? { color: '#fff' } : undefined}>{Math.round(share)}%</em>}
              </span>
              <span className="fs-v">
                <b>{numberFmt.format(s.count)}</b>
                <small>
                  {i ? (prev ? `${Math.round((s.count / prev) * 100)}% of previous` : '—') : unit}
                </small>
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Seven rows (Monday first) of 24 hours. */
export function HeatMap({ grid, what = 'Sign-ups' }: { grid: number[][]; what?: string }) {
  const { bind, node } = useTip()
  const max = Math.max(1, ...grid.flat())
  return (
    <div style={{ overflowX: 'auto' }}>
      <div className="heat" style={{ minWidth: 420 }}>
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="hd">
            {h % 3 === 0 ? h : ''}
          </span>
        ))}
        {DAYS.map((d, di) => (
          <div key={d} style={{ display: 'contents' }}>
            <span className="dl">{d}</span>
            {(grid[di] ?? []).map((v, h) => (
              <span
                key={h}
                className="c"
                style={{ background: sequential(v / max) }}
                {...bind(
                  <TipRows
                    title={`${d}, ${h}:00–${h + 1}:00`}
                    rows={[{ label: what, value: numberFmt.format(v) }]}
                  />,
                )}
              />
            ))}
          </div>
        ))}
      </div>
      {node}
    </div>
  )
}

export function HeatScale() {
  return (
    <span className="heat-scale">
      Fewer
      {[0.05, 0.3, 0.55, 0.8, 1].map((f) => (
        <i key={f} style={{ background: sequential(f) }} />
      ))}
      More
    </span>
  )
}
